import * as React from "react";
import { Form, FormElement } from "@progress/kendo-react-form";
import { Button } from "@progress/kendo-react-buttons";
import { Stepper } from "@progress/kendo-react-layout";
import { EventFormMainInfo } from "./EventFormMainInfo";
import { EventFormEventTypeInfo } from "./EventFormEventTypeInfo";
import { EventFormAdditionalInfo } from "./EventFormAdditionalInfo";
import { useTranslation } from "react-i18next";
import EventService from "../../../services/srv-event/EventService";
import { useState } from "react";
import { useCallback } from "react";
import { TIMEZONE } from "../../../config/globalVariables";
import { getLocalDate } from "../../../utils/common";

const stepPages = [
  EventFormMainInfo,
  EventFormEventTypeInfo,
  EventFormAdditionalInfo,
];

export const EventForm = (props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [additionalFields, setAdditionalFields] = useState([]);
  const [step, setStep] = useState(0);
  const [formState, setFormState] = useState(props.event || {});
  const [steps, setSteps] = React.useState([
    { label: t("event_main_info"), isValid: undefined },
    { label: t("event_type_related_info"), isValid: undefined },
    { label: t("additional_info"), isValid: undefined },
  ]);

  const lastStepIndex = steps.length - 1;
  const isLastStep = lastStepIndex === step;

  const isPreviousStepsValid = steps
    .slice(0, step)
    .every((step) => step.isValid !== false);

  const onStepSubmit = useCallback(
    (dataItem) => {
      const { isValid, values } = dataItem;
      const currentSteps = steps.map((currentStep, index) => ({
        ...currentStep,
        isValid: index === step ? isValid : currentStep.isValid,
      }));
      setSteps(currentSteps);
      if (!isValid) {
        return;
      }
      setFormState(values);
      setStep(() => Math.min(step + 1, lastStepIndex));
      if (isLastStep) {
        handleSubmit(values);
      }
    },
    [steps, isLastStep, step, lastStepIndex]
  );

  const handleSubmit = async (dataItem) => {
    setLoading(true);
    const additionalData = additionalFields.reduce((obj, field) => {
      if (dataItem.hasOwnProperty(field.alaN_KODU)) {
        obj[field.alaN_KODU] = dataItem[field.alaN_KODU];
      }
      return obj;
    }, {});

    const resultData = Object.keys(dataItem).reduce((obj, key) => {
      if (!additionalFields.some((field) => field.alan === key)) {
        obj[key] = dataItem[key];
      }
      return obj;
    }, {});
    resultData["eK_ALANLAR"] = JSON.stringify(additionalData);
    resultData["baS_TAR"] = getLocalDate(resultData.baS_TAR, TIMEZONE);
    resultData["biT_TAR"] = getLocalDate(resultData.biT_TAR, TIMEZONE);
    try {
      if (props.isUpdate) {
        // Güncelleme işlemi
        await EventService.updateEvent(resultData);
      } else {
        // Yeni kayıt işlemi
        await EventService.addEvent(resultData);
      }
      props.toggleDialog();
      props.setIsAdded(true);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onPrevClick = React.useCallback(
    (event) => {
      event.preventDefault();
      setStep(() => Math.max(step - 1, 0));
    },
    [step, setStep]
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Stepper value={step} items={steps} style={{ marginBottom: "10px" }} />
      <Form
        initialValues={formState}
        onSubmitClick={onStepSubmit}
        render={(formRenderProps) => (
          <div
            style={{
              width: "100%",
              maxWidth: "500px",
              margin: "0 auto",
              padding: "0 20px",
            }}
          >
            <FormElement
              style={{
                width: "100%",
              }}
            >
              {React.createElement(stepPages[step], {
                formRenderProps,
                setAdditionalFields,
                additionalFields,
                initialValues: formState,
              })}
              <span
                style={{ marginTop: "40px" }}
                className={"k-form-separator"}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignContent: "center",
                  width: "100%",
                }}
              >
                <span style={{ alignSelf: "center" }}>{step + 1} / 3</span>
                <div>
                  {step !== 0 && (
                    <Button
                      style={{ marginRight: "16px" }}
                      onClick={onPrevClick}
                    >
                      {t("previous")}
                    </Button>
                  )}
                  <Button disabled={loading} onClick={formRenderProps.onSubmit}>
                    {loading ? t("saving") : isLastStep ? t("save") : t("next")}
                  </Button>
                </div>
              </div>
            </FormElement>
          </div>
        )}
      />
    </div>
  );
};
